/**
 * Live-session identity resolvers.
 *
 * INVARIANT: any Supabase Storage path or RLS-checked column (a `*_id` the
 * policy compares to `auth.uid()`) MUST be built from the LIVE session identity
 * — the token the request will actually send — never from a hook-cached
 * `user.id`. A cached id can drift from the live session (account switching, a
 * token refresh, a missed auth event); the write then targets the wrong folder
 * / id and Postgres rejects it with "new row violates row-level security
 * policy". This is the exact class the avatar upload hit; these helpers are the
 * one correct way to resolve identity at write time.
 */
import { supabase } from '../supabase'
import { ServiceError } from '../errors/service-error'

/**
 * Server-validated live user id. `getUser()` revalidates and refreshes an
 * expired token before returning, so this is the safest resolver for
 * infrequent, high-stakes writes (profile update, KYC uploads, disputes).
 * Throws ServiceError.unauthorized when there is no live session.
 */
export async function requireLiveUserId(): Promise<string> {
  const { data, error } = await supabase.auth.getUser()
  const id = data?.user?.id
  if (error || !id) {
    throw ServiceError.unauthorized(`No live session (${error?.message ?? 'no user'})`)
  }
  return id
}

/**
 * Live user id from the locally-stored session (no network round-trip). Use for
 * hot paths (messaging) where `getUser()`'s server call would add latency per
 * action. It is still the exact token the request will send, so RLS evaluates
 * `auth.uid()` to this id. Throws when there is no session.
 */
export async function currentUserIdOrThrow(): Promise<string> {
  const { data } = await supabase.auth.getSession()
  const id = data?.session?.user?.id
  if (!id) throw ServiceError.unauthorized('No active session')
  return id
}
